import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, Store, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { CountryCitySelect } from "@/components/CountryCitySelect";
import { RichTextField } from "@/components/RichTextField";
import { getCurrencySymbol } from "@/lib/currencyUtils";
import {
  addItemTypeForCategory,
  buildProductPayload,
  buildSupplierPayload,
  itemName,
  suggestedPerPersonPrice,
  type CustomItemSource,
  type InventoryKind,
} from "@/lib/customItemToInventory";

const NEW_SUPPLIER = "__new__";

interface Props {
  item: CustomItemSource | null;
  leadId: string;
  currency: string;
  paxCount: number;
  onClose: () => void;
}

export function SaveToInventoryDialog({ item, leadId, currency, paxCount, onClose }: Props) {
  const qc = useQueryClient();
  const open = !!item;

  const [kind, setKind] = useState<InventoryKind>("product");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [price, setPrice] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [newSupplierName, setNewSupplierName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const { data: categories = [] } = useQuery({
    queryKey: ["supplier-categories-for-inventory"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase.from("supplier_categories").select("id, name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers-for-inventory"],
    enabled: open && kind === "product",
    queryFn: async () => {
      const { data, error } = await supabase.from("suppliers").select("id, name, city, territory").order("name");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: lead } = useQuery({
    queryKey: ["lead-brand-for-inventory", leadId],
    enabled: open,
    queryFn: async () => {
      const { data } = await supabase.from("leads").select("brand_id").eq("id", leadId).maybeSingle();
      return data;
    },
  });

  // Reset and prefill from the custom item each time the dialog opens.
  useEffect(() => {
    if (!item) return;
    setKind("product");
    const n = itemName(item);
    setName(n);
    setDescription(item.custom_description || item.description || "");
    setPrice(suggestedPerPersonPrice(item, paxCount)?.toString() ?? "");
    setSupplierId("");
    setNewSupplierName(n);
    setEmail("");
    setPhone("");
    setCountry("");
    setCity("");
    const loc = (item.location || "").trim();
    if (!loc) return;
    (async () => {
      const { data: cityRow } = await supabase
        .from("cities")
        .select("name, countries(name)")
        .ilike("name", loc)
        .maybeSingle();
      if (cityRow) {
        setCountry((cityRow as any).countries?.name || "");
        setCity(cityRow.name);
        return;
      }
      const { data: countryRow } = await supabase.from("countries").select("name").ilike("name", loc).maybeSingle();
      if (countryRow) setCountry(countryRow.name);
    })();
  }, [item, paxCount]);

  // Sensible default category per kind.
  useEffect(() => {
    if (!open || !categories.length) return;
    const isConference = (item as any)?.item_type === "conference";
    const wanted = isConference ? "Conference" : kind === "product" ? "Activity" : "Venue";
    const found = categories.find((c) => c.name === wanted);
    setCategoryId(found?.id || "");
  }, [kind, open, categories, item]);

  // Warn (not block) when something with the same name already exists.
  const trimmedName = name.trim();
  const { data: duplicates = [] } = useQuery({
    queryKey: ["inventory-dupe-check", kind, trimmedName.toLowerCase()],
    enabled: open && trimmedName.length >= 3,
    queryFn: async () => {
      if (kind === "product") {
        const { data } = await supabase.from("products").select("id, product_name, city").ilike("product_name", trimmedName).limit(3);
        return (data || []).map((d) => `${d.product_name}${d.city ? ` (${d.city})` : ""}`);
      }
      const { data } = await supabase.from("suppliers").select("id, name, city").ilike("name", trimmedName).limit(3);
      return (data || []).map((d) => `${d.name}${d.city ? ` (${d.city})` : ""}`);
    },
  });

  const supplierOptions = useMemo(
    () => [
      { value: NEW_SUPPLIER, label: "+ Create a new supplier", keywords: "new create add" },
      ...suppliers.map((s: any) => ({
        value: s.id,
        label: `${s.name}${s.city ? ` — ${s.city}` : s.territory ? ` — ${s.territory}` : ""}`,
        keywords: `${s.name} ${s.city || ""} ${s.territory || ""}`,
      })),
    ],
    [suppliers],
  );

  const categoryName = categories.find((c) => c.id === categoryId)?.name;
  const findUnder = addItemTypeForCategory(categoryName);

  const save = useMutation({
    mutationFn: async () => {
      if (!item) throw new Error("No item");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You're signed out. Please sign in again.");

      if (kind === "supplier") {
        const payload = buildSupplierPayload({
          item, name: trimmedName, description: description || null, categoryId: categoryId || null,
          country: country || null, city: city || null, email: email || null, phone: phone || null, userId: user.id,
        });
        const { error } = await supabase.from("suppliers").insert(payload as any);
        if (error) throw error;
        await supabase.from("activity_log").insert({
          lead_id: leadId, user_id: user.id, action: "Custom item saved as supplier",
          details: `"${trimmedName}" was added to Suppliers from this itinerary.`,
        });
        return { label: "supplier" };
      }

      let finalSupplierId = supplierId;
      if (supplierId === NEW_SUPPLIER) {
        const supName = newSupplierName.trim();
        if (!supName) throw new Error("Enter the new supplier's name.");
        const { data: sup, error: supErr } = await supabase
          .from("suppliers")
          .insert(buildSupplierPayload({
            item, name: supName, description: null, categoryId: categoryId || null,
            country: country || null, city: city || null, email: email || null, phone: phone || null, userId: user.id,
          }) as any)
          .select("id")
          .single();
        if (supErr) throw supErr;
        finalSupplierId = sup.id;
      }

      const priceNum = price.trim() ? Number(price) : null;
      const payload = buildProductPayload({
        item, name: trimmedName, description: description || null, categoryId, supplierId: finalSupplierId,
        city: city || null, pricePerPerson: priceNum != null && !Number.isNaN(priceNum) ? priceNum : null,
        currency, brandId: (lead as any)?.brand_id ?? null, userId: user.id,
      });
      const { error } = await supabase.from("products").insert(payload as any);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        lead_id: leadId, user_id: user.id, action: "Custom item saved as product",
        details: `"${trimmedName}" was added to Products (internal only) from this itinerary.`,
      });
      return { label: "product" };
    },
    onSuccess: ({ label }) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["products-by-item-type"] });
      qc.invalidateQueries({ queryKey: ["suppliers-by-item-type"] });
      qc.invalidateQueries({ queryKey: ["all-suppliers-for-itinerary"] });
      qc.invalidateQueries({ queryKey: ["suppliers-for-inventory"] });
      qc.invalidateQueries({ queryKey: ["inventory-saved-items", leadId] });
      qc.invalidateQueries({ queryKey: ["unified-activity-feed"] });
      toast.success(
        label === "product"
          ? `Saved as a product. Find it in Add Item under "${findUnder || "its category"}".`
          : `Saved as a supplier. Find it in Add Item under "Supplier"${findUnder ? ` or "${findUnder}"` : ""}.`,
      );
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || "Couldn't save this item"),
  });

  const canSave =
    !!trimmedName &&
    !save.isPending &&
    (kind === "supplier" || (!!categoryId && !!supplierId && (supplierId !== NEW_SUPPLIER || !!newSupplierName.trim())));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Save for reuse</DialogTitle>
          <DialogDescription>
            Turn this custom item into something you can pick from Add Item on any lead. This itinerary item stays as it is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup value={kind} onValueChange={(v) => setKind(v as InventoryKind)} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Label htmlFor="inv-product" className="flex items-start gap-2 rounded-md border p-3 cursor-pointer has-[:checked]:border-primary">
              <RadioGroupItem id="inv-product" value="product" className="mt-0.5" />
              <span>
                <span className="flex items-center gap-1 font-medium"><Package className="h-4 w-4" /> Product</span>
                <span className="block text-xs text-muted-foreground font-normal">A tour or activity, priced per person</span>
              </span>
            </Label>
            <Label htmlFor="inv-supplier" className="flex items-start gap-2 rounded-md border p-3 cursor-pointer has-[:checked]:border-primary">
              <RadioGroupItem id="inv-supplier" value="supplier" className="mt-0.5" />
              <span>
                <span className="flex items-center gap-1 font-medium"><Store className="h-4 w-4" /> Supplier</span>
                <span className="block text-xs text-muted-foreground font-normal">A venue, restaurant or company</span>
              </span>
            </Label>
          </RadioGroup>

          <div className="space-y-2">
            <Label htmlFor="inv-name">Name *</Label>
            <Input id="inv-name" value={name} onChange={(e) => setName(e.target.value)} />
            {duplicates.length > 0 && (
              <p className="flex items-start gap-1 text-xs text-accent-foreground bg-accent rounded p-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>A {kind} with this name already exists: {duplicates.join(", ")}. You can still save another.</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Category{kind === "product" ? " *" : ""}</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {findUnder && (
              <p className="text-xs text-muted-foreground">
                In Add Item, choose <span className="font-medium">{kind === "supplier" ? `Supplier or ${findUnder}` : findUnder}</span> to find it.
              </p>
            )}
          </div>

          <CountryCitySelect
            country={country}
            city={city}
            onCountryChange={(c) => { setCountry(c); setCity(""); }}
            onCityChange={setCity}
            idPrefix="inv"
          />

          {kind === "product" && (
            <>
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <SearchableSelect
                  options={supplierOptions}
                  value={supplierId}
                  onValueChange={setSupplierId}
                  placeholder="Who provides this?"
                  searchPlaceholder="Search suppliers..."
                />
                {supplierId === NEW_SUPPLIER && (
                  <div className="space-y-2 rounded-md border p-3">
                    <Label htmlFor="inv-new-sup">New supplier name *</Label>
                    <Input id="inv-new-sup" value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="inv-price">Price per person ({getCurrencySymbol(currency)})</Label>
                <Input id="inv-price" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
                {item && item.custom_price_basis !== "per_person" && Number(item.cost || item.manual_price) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    This item had a flat total of {getCurrencySymbol(currency)}{Number(item.cost || item.manual_price).toLocaleString()}, split here across {paxCount} {paxCount === 1 ? "person" : "people"}. Change it if needed.
                  </p>
                )}
              </div>

              <p className="text-xs text-muted-foreground rounded bg-muted p-2">
                New products stay off the public booking website. You can switch that on later in Products.
              </p>
            </>
          )}

          {(kind === "supplier" || supplierId === NEW_SUPPLIER) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="inv-email">Supplier email</Label>
                <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-phone">Supplier phone</Label>
                <Input id="inv-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="inv-desc">Description</Label>
            <RichTextField id="inv-desc" value={description} onChange={setDescription} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? "Saving..." : kind === "product" ? "Save as product" : "Save as supplier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
